
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import UserChannels from "@/components/UserChannels";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Fuel, CircuitBoard, Building2, BookOpen, Blocks, GraduationCap, Presentation, Briefcase, Atom, Cog, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";

const TreyProfile = () => {
  const { t } = useTranslation();
  const { currentLanguage, setLanguage } = useLanguage();
  const user = {
    name: "Trey Fleming",
    role: "iSPAI Founder",
    email: "trey.fleming@ispai.io",
    solutionBounties: 4,
    domains: [
      "AI",
      "Web3",
      "Innovation",
      "Knowledge Management",
      "Blockchain",
      "Decentralization",
      "Strategic Planning",
      "Start Ups"
    ],
    industries: [
      { name: "Oil and Gas", icon: Fuel },
      { name: "Energy", icon: Atom },
      { name: "Government", icon: Briefcase },
      { name: "Consulting", icon: Presentation },
      { name: "Systems Integration", icon: Cog },
      { name: "Sports", icon: Building2 }
    ],
    confirmedSolutions: 42,
    confirmedKnowledgeBases: 86,
    tokenBalance: 250
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/bf1f7230-16a9-49f6-963d-7acbce3901ce.png"
                    imageAlt="Trey Fleming"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/bf1f7230-16a9-49f6-963d-7acbce3901ce.png" 
                        alt="Trey Fleming"
                      />
                      <AvatarFallback>TF</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <UserChannels />
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">{t('profile.industries')}</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ImageHoverPopup 
                      imageSrc="/lovable-uploads/bf1f7230-16a9-49f6-963d-7acbce3901ce.png"
                      imageAlt="Trey Fleming"
                    >
                      <h2 className="font-serif text-3xl font-bold text-black cursor-pointer hover:text-primary transition-colors">{user.name}</h2>
                    </ImageHoverPopup>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="flex items-center mb-4">
                    <p className="text-lg font-medium text-black">{user.role}</p>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">{t('profile.solutionBounties')}</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/trey"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {t('profile.viewDetails')}
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.2} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">{t('profile.confirmedSolutions')}</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">{t('profile.knowledgeBases')}</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">{t('profile.domains')}</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TreyProfile;
